import { useState } from "react";
import { ArticlesSection } from "@/components/receipt/article-section/ArticlesSection";
import { BottomActions } from "@/components/receipt/BottomActions";
import { GoodbyeSection } from "@/components/receipt/GoodbyeSection";
import { Header } from "@/components/receipt/Header";
import { MerchantInfo } from "@/components/receipt/MerchantInfo";
import { PaymentSection } from "@/components/receipt/PaymentSection";
import { PopupWrapper } from "@/components/receipt/popups/PopupWrapper";
import { RegnskogSection } from "@/components/receipt/RegnskogSection";
import { TotalSection } from "@/components/receipt/TotalSection";
import { InvoiceSection } from "@/components/receipt/InvoiceSection";
import { ElectronicCopyBanner } from "@/components/receipt/ElectronicCopyBanner";
import { Receipt, ExtendedViewItem } from "@/types/receipt";
import {
  GLOBAL_WIDGET_TYPES,
  PopupState,
  CLOSED_POPUP_STATE,
  QUICK_SEND_ACCOUNTING_POPUP_TYPE,
} from "@/constants/popup";

const POPUP_TYPES_REQUIRING_EXTENDED_DATA = new Set<string>([
  "extended_view",
  QUICK_SEND_ACCOUNTING_POPUP_TYPE,
  ...GLOBAL_WIDGET_TYPES,
]);

const popupRequiresExtendedViewData = (type: string): boolean =>
  POPUP_TYPES_REQUIRING_EXTENDED_DATA.has(type) || type.startsWith("widget_");

interface ReceiptViewProps {
  receipt: Receipt;
  discountBarcodeImages: string[];
  ticketBarcodeImages: { [articleIndex: string]: string[] };
  mainReceiptBarcodeImage: string;
  lang: string;
  template: string;
  logoReference: string | null;
  user_id: string;
  receipt_id: string;
  isElectronicCopy?: boolean;
  extended_view_items?: ExtendedViewItem[];
  extended_view_images?: { [key: string]: string };
  email?: string | null;
  phoneNumber?: string | null;
  storeLocationUrl?: string | null;
}

export const ReceiptView = ({
  receipt,
  discountBarcodeImages,
  mainReceiptBarcodeImage,
  ticketBarcodeImages,
  lang: initialLang,
  template,
  logoReference = null,
  user_id,
  receipt_id,
  isElectronicCopy,
  extended_view_items,
  extended_view_images,
  email,
  phoneNumber,
  storeLocationUrl,
}: ReceiptViewProps) => {
  const [popupOpen, setPopupOpen] = useState<PopupState>(CLOSED_POPUP_STATE);
  const [expandedTotal, setExpandedTotal] = useState(false);
  const [lang, setLang] = useState(initialLang);

  const openPopup = (
    type: string,
    index?: number,
    article?: PopupState["article"]
  ) => {
    setPopupOpen({ type, index: index ?? -1, article });
  };

  // Determine if PopupWrapper should render
  const hasExtendedViewData =
    extended_view_items &&
    extended_view_items.length > 0 &&
    extended_view_images;
  const canRenderPopup =
    popupOpen.type !== "" &&
    (!popupRequiresExtendedViewData(popupOpen.type) || hasExtendedViewData);

  return (
    <div className={`template template-${template}`}>
      <div className="wrapper">
        {isElectronicCopy && <ElectronicCopyBanner lang={lang} />}
        <div className="receipt-wrapper">
          <div className="receipt">
            <Header
              lang={lang}
              onLanguageChange={setLang}
              user_id={user_id}
              zeipt_receipt_transnr={receipt_id}
              receipt={receipt}
              storeLocationUrl={storeLocationUrl}
            />

            <MerchantInfo
              receipt={receipt}
              lang={lang}
              logoReference={logoReference}
              extended_view_items={extended_view_items}
              extended_view_images={extended_view_images}
              user_id={user_id}
              receipt_id={receipt_id}
              email={email}
              phoneNumber={phoneNumber}
            />

            <ArticlesSection
              receipt={receipt}
              lang={lang}
              openPopup={openPopup}
              extended_view_items={extended_view_items}
              extended_view_images={extended_view_images}
              user_id={user_id}
              receipt_id={receipt_id}
              email={email}
              phoneNumber={phoneNumber}
            />

            <TotalSection
              receipt={receipt}
              lang={lang}
              expandedTotal={expandedTotal}
              setExpandedTotal={setExpandedTotal}
            />

            {receipt.payments?.map((payment, index) => (
              <PaymentSection
                key={"payment-" + index}
                index={index}
                payment={payment}
                lang={lang}
                receipt={receipt}
              />
            ))}

            <InvoiceSection receipt={receipt} lang={lang} />

            <GoodbyeSection receipt={receipt} lang={lang} />

            <RegnskogSection lang={lang} />

            <BottomActions
              receipt={receipt}
              lang={lang}
              onAction={openPopup}
              extendedViewItems={extended_view_items}
              extendedViewImages={extended_view_images}
            />

            {canRenderPopup && (
              <PopupWrapper
                receipt={receipt}
                lang={lang}
                popupOpen={popupOpen}
                showPopup={setPopupOpen}
                mainReceiptBarcodeImage={mainReceiptBarcodeImage}
                discountBarcodeImages={discountBarcodeImages}
                ticketBarcodeImages={ticketBarcodeImages}
                extendedViewItems={extended_view_items}
                extendedViewImages={extended_view_images}
                user_id={user_id}
                receipt_id={receipt_id}
                email={email}
                phoneNumber={phoneNumber}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
